import React, { useState } from 'react';
import { Mail, Send, Check } from 'lucide-react';
import './NewsletterSignup.css';

interface NewsletterSignupProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'compact';
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ 
  title = "Stay Updated", 
  description = "Get the latest news, tour dates, and exclusive content delivered to your inbox.",
  variant = 'default'
}) => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    const utm: { [key: string]: string } = {};
    
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    
    if (utmSource) utm.utm_source = utmSource;
    if (utmMedium) utm.utm_medium = utmMedium;
    if (utmCampaign) utm.utm_campaign = utmCampaign;
    
    return utm;
  };

  const getPageMetadata = () => ({
    source_page: window.location.href,
    referrer: document.referrer,
    page_title: document.title,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;

    setIsLoading(true);
    setError('');

    const utm = getUTMParams();
    const meta = getPageMetadata();

    try {
      const response = await fetch('https://us-central1-trinity-452312.cloudfunctions.net/thekults-newsletter-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          consent_given: true,
          ...utm,
          ...meta,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setIsSubscribed(true);
        setEmail('');
      } else {
        setError(data.error || 'Subscription failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className={`newsletter-signup ${variant}`}>
        <div className="newsletter-success">
          <Check size={24} />
          <h3>Successfully Subscribed!</h3>
          <p>You'll receive updates from The Kults soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`newsletter-signup ${variant}`}>
      <div className="newsletter-content">
        <div className="newsletter-header">
          <Mail size={variant === 'compact' ? 20 : 24} />
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="newsletter-form">
          <div className="email-input-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={isLoading}
              className="email-input"
            />
            {error && <div className="newsletter-error">{error}</div>}
            <button 
              type="submit" 
              disabled={isLoading || !email}
              className="subscribe-btn"
            >
              {isLoading ? (
                <div className="loading-spinner" />
              ) : (
                <Send size={variant === 'compact' ? 16 : 18} />
              )}
            </button>
          </div>
        </form>
        
        <p className="newsletter-disclaimer">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </div>
    </div>
  );
};

export default NewsletterSignup; 